import { getPEG_DAAvgYearly } from "$/api/market-monitoring/gas-analysis.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import BarGraph from "$/lib/components/graphs/BarGraph";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import { cn, getArrayKeys } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const COLORS = [
  "#d2eed2",
  "#a8dba8",
  "#7fc87f",
  "#56b556",
  "#2d992d",
  "#187a18",
  "#065a06",
];

const GasPegDailyAveragesBarChart = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const { data, isLoading } = useQuery({
    queryKey: ["get-gas-peg-daily-averages"],
    queryFn: () => getPEG_DAAvgYearly(),
  });

  const toDisplay = useMemo(() => (data ? data : []), [data]);

  const keys = useMemo(
    () => getArrayKeys(toDisplay, "month").sort(),
    [toDisplay],
  );

  const dataKeys = useMemo(
    () =>
      keys.map((key, i) => ({
        key,
        color: COLORS[i] ?? "",
      })),
    [keys],
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const last3Years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from(new Array(3), (_, i) => currentYear - i);
  }, []);

  const defaultGraphVisibilityKeys = useMemo(
    () =>
      keys.map((key, i) => ({
        name: key,
        isVisible: last3Years.includes(parseInt(key)),
        color: COLORS[i],
      })),
    [keys, last3Years],
  );

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    if (key === "Tout") {
      if (value === true)
        setGraphVisibility([
          { name: "Tout", isVisible: true, color: "" },
          ...defaultGraphVisibilityKeys.map((item) => ({
            ...item,
            isVisible: true,
          })),
        ]);
      else
        setGraphVisibility([
          { name: "Tout", isVisible: false, color: "" },
          ...defaultGraphVisibilityKeys.map((item) => ({
            ...item,
            isVisible: false,
          })),
        ]);

      return;
    }

    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  useEffect(() => {
    setGraphVisibility([
      { name: "Tout", isVisible: true, color: "" },
      ...defaultGraphVisibilityKeys,
    ]);
  }, [keys, defaultGraphVisibilityKeys]);

  return (
    <DisplayBox
      title="Spot moyen mensuel par année PEG"
      Icon={LineChartIcon}
      theme="green"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer className="!h-[400px]">
        <Flexbox fullWidth className="px-2">
          {isLoading ? (
            <Flexbox fullWidth fullHeight align="center" justify="center">
              <Loader height={40} width={40} />
            </Flexbox>
          ) : (
            <Flexbox fullWidth className="h-[95%]">
              <BarGraph
                data={data ?? []}
                xDataKey="month"
                yAxisLabel="Prix mensuel moyen (€/MWh)"
                dataKeys={dataKeys}
                unit=" €/MWh"
                graphVisibility={graphVisibility}
                theme="green"
                withLabel={false}
              />
            </Flexbox>
          )}

          <GraphFilter
            row
            data={graphVisibility}
            handleOnChange={handleOnChangeGraphVisiblity}
          />
        </Flexbox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default GasPegDailyAveragesBarChart;
